import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faFileInvoiceDollar, faFolderOpen, faBuilding, faListCheck } from '@fortawesome/free-solid-svg-icons';
import { MobileDrawer } from './MobileDrawer';
import { QuickTaskModal } from '../dashboard/QuickTaskModal';
import { useMenuLabels } from '../../lib/hooks/useMenuLabels';

interface QuickCreateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CreateOption {
  id: string;
  label: string;
  icon: any;
  action: 'navigate' | 'modal';
  path?: string;
}

export function QuickCreateDrawer({ isOpen, onClose }: QuickCreateDrawerProps) {
  const navigate = useNavigate();
  const [showTaskModal, setShowTaskModal] = useState(false);
  const { lead: leadLabel, client: clientLabel } = useMenuLabels();

  const createOptions: CreateOption[] = useMemo(() => [
    {
      id: 'lead',
      label: `${leadLabel.nuovo} ${leadLabel.singular}`,
      icon: faUserPlus,
      action: 'navigate',
      path: '/leads?create=true'
    },
    {
      id: 'deal',
      label: 'Nuova Offerta',
      icon: faFileInvoiceDollar,
      action: 'navigate',
      path: '/deals?create=true'
    },
    {
      id: 'project',
      label: 'Nuovo Progetto',
      icon: faFolderOpen,
      action: 'navigate',
      path: '/projects?create=true'
    },
    {
      id: 'client',
      label: `${clientLabel.nuovo} ${clientLabel.singular}`,
      icon: faBuilding,
      action: 'navigate',
      path: '/clients?create=true'
    },
    {
      id: 'task',
      label: 'Nuovo Task',
      icon: faListCheck,
      action: 'modal'
    }
  ], [leadLabel, clientLabel]);

  const handleOptionClick = (option: CreateOption) => {
    onClose();

    if (option.action === 'navigate' && option.path) {
      navigate(option.path);
    } else if (option.action === 'modal' && option.id === 'task') {
      // Delay modal open to allow drawer to close
      setTimeout(() => setShowTaskModal(true), 100);
    }
  };

  return (
    <>
      <MobileDrawer
        isOpen={isOpen}
        onClose={onClose}
        title="Crea nuovo"
        height="auto"
        showHandle
        swipeToClose
      >
        <div className="space-y-2 pb-4">
          {createOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleOptionClick(option)}
              className="w-full flex items-center gap-4 px-4 py-4 rounded-xl bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors active:scale-[0.98]"
            >
              <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={option.icon}
                  className="h-5 w-5 text-gray-700 dark:text-gray-300"
                />
              </div>
              <span className="text-base font-medium text-gray-800 dark:text-gray-200">
                {option.label}
              </span>
            </button>
          ))}
        </div>
      </MobileDrawer>

      {/* Task Modal */}
      <QuickTaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        onTaskCreated={() => {
          setShowTaskModal(false);
        }}
      />
    </>
  );
}

export default QuickCreateDrawer;
